import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { isProdEnvironment } from '@bingads-webui-campaign/campaign-ui-common';
import { useOpenPopupAndWaitForAuth } from '@bingads-webui-campaign-react/common-hooks';

const LinkedInConfig = {
  clientId: {
    Prod: '86blf1re65drv3',
    Test: '86y8qo6y8w9zty',
  },
  scope: [
    'rw_ads',
    'r_basicprofile',
    'w_organization_social',
    'w_member_social',
    'rw_organization_admin',
    'r_ads_reporting',
    'rw_conversions',
  ],
  url: 'https://www.linkedin.com/oauth/v2/authorization',
};

const defaultActivityLogger = {
  trace: _.noop,
  error: _.noop,
};

export function useLinkedInLinking({
  config = LinkedInConfig,
  windowObject,
}) {
  const { scope: { values: { cid, aid } } } = useContext(ScopeContext);
  const isProd = isProdEnvironment(windowObject.location.host);
  const openPopupAndWaitForAuth = useOpenPopupAndWaitForAuth(windowObject);

  const login = useCallback((activity = defaultActivityLogger) => {
    const contextValue = `LinkedIn-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
    const baseCallbackUrl = `${windowObject.location.origin}/campaign/vnext/createcampaign/linkedin`;
    const cbUrlSearchParams = new URLSearchParams();

    // add the cid/aid variables to callback URL, so we don't need to rely on CCUI redirect
    // this is required in the multi-user scenario
    cbUrlSearchParams.append('cid', cid);
    cbUrlSearchParams.append('aid', aid);
    const callbackUrl = `${baseCallbackUrl}?${cbUrlSearchParams}`;

    const targetUrl = new URL(config.url);
    const authSearchParams = {
      response_type: 'code',
      client_id: isProd ? config.clientId.Prod : config.clientId.Test,
      redirect_uri: callbackUrl,
      state: contextValue,
      scope: config.scope.join(' '),
    };

    _.each(authSearchParams, (value, key) => {
      targetUrl.searchParams.set(key, value);
    });

    activity.trace(`LinkedIn auth started: state=${contextValue}, callbackUrl=${callbackUrl}`);
    return openPopupAndWaitForAuth({
      url: targetUrl,
      matcher: { state: contextValue },
    }).then((result) => {
      const AuthorizationCode = result.code;
      if (!AuthorizationCode) {
        throw new Error('no_auth_code_returned');
      }
      activity.trace(`LinkedIn auth completed, code.length=${AuthorizationCode.length}`);

      // return AuthorizationData object
      return {
        AuthorizationCode,
        RedirectUrl: authSearchParams.redirect_uri,
      };
    });
  }, [aid, cid, config, isProd, openPopupAndWaitForAuth, windowObject]);

  return login;
}
